/**
 * @module support/state-object-hash-utilities
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */

/* tslint:disable:no-any */
// We are not able to get the exact type of variable so need to discuss with concerned developer

// Core Libraries
import { Injectable } from '@angular/core';

import { Observer, BehaviorSubject } from 'rxjs';
import * as objectPath from 'object-path';
import { ObjectError } from '../models/object-error';

// We need to enable type `any` because the user can provide `any` value to the store.

@Injectable()
export class ObjectService {
  /**
   * Stores value of objectErrors
   */
  public objectErrors: BehaviorSubject<ObjectError>;

  constructor() {
    this.objectErrors = new BehaviorSubject<ObjectError>(new ObjectError());
  }

  /**
   * Fetches the object from the specified path
   * @param obj
   * @param path
   * @param defaultValue
   * @returns any
   */
  public get(obj: object, path: string, defaultValue?: any): any {
    if (objectPath.has(obj, path)) {
      return objectPath.get(obj, path);
    } else {
      this.emitObjectError(obj, path);
      return defaultValue;
    }
  }

  /**
   * Fetches the object from the specified path
   * @param obj
   * @param path
   * @param defaultValue
   * @returns any
   */
  public has(obj: object, path: string): any {
    return objectPath.has(obj, path);
  }

  /**
   * Sets the object value at the specified path
   * @param obj
   * @param path
   * @param value
   * @returns void
   */
  public set(obj: object, path: string, value: any): void {
    objectPath.set(obj, path, value);
  }

  /**
   * Destroys the object value from the specified path
   * @param obj
   * @param path
   * @returns void
   */
  public destroy(obj: object, path: string): void {
    if (obj && path) {
      objectPath.del(obj, path);
    }
  }

  /**
   * Sets a watcher to Observer object
   * @param o
   * @returns void
   */
  public watchErrors(o: Observer<ObjectError>): void {
    this.objectErrors.subscribe(o);
  }

  /**
   * Emits the object error to the specified path
   * @param obj
   * @param path
   * @returns void
   */
  private emitObjectError(obj: object, path: string): void {
    let steps = path.split('.') || [];
    let increment = '';
    while (steps.length) {
      increment += (increment !== '' ? '.' : '') + steps.shift();
      if (!objectPath.has(obj, increment)) {
        this.objectErrors.error(this.buildObjectError(obj, path, increment));
        steps = []; // kill while loop
      }
    }
  }

  /**
   * Fetches the error object form the specified path
   * @param obj
   * @param path
   * @param step
   * @returns {object}
   */
  private buildObjectError(obj: object, path: string, step: string): ObjectError {
    return {
      obj: obj,
      path: path,
      message: `undefined element at ${step}`,
    };
  }
}
